import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import { FaArrowLeft, FaBell, FaUser } from 'react-icons/fa';
import { RealtimeChannel } from '@supabase/supabase-js';
import type { Database } from '../../lib/supabase';
import './chat.scss';

type Message = Database['public']['Tables']['messages']['Row'];
type UserStatus = Database['public']['Tables']['user_status']['Row'];

interface ChatUser {
  id: string;
  email: string;
  firstName: string;
  last_message?: string;
  last_message_time?: string;
  unread_count: number;
}

export function Chat() {
  const { user } = useAuth();
  const [messages, setMessages] = useState<Message[]>([]);
  const [newMessage, setNewMessage] = useState('');
  const [unreadCount, setUnreadCount] = useState(0);
  const [adminStatus, setAdminStatus] = useState<UserStatus | null>(null);
  const [users, setUsers] = useState<ChatUser[]>([]);
  const [selectedUser, setSelectedUser] = useState<ChatUser | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const [messageChannel, setMessageChannel] = useState<RealtimeChannel | null>(null);
  const [statusChannel, setStatusChannel] = useState<RealtimeChannel | null>(null);
  const ADMIN_ID = process.env.REACT_APP_ADMIN_USER_ID;
  const isAdmin = user?.id === ADMIN_ID;

  useEffect(() => {
    console.log('Current user:', user?.id);
    console.log('Admin ID:', ADMIN_ID);
    console.log('Is admin?', isAdmin);
    
    if (user) {
      if (isAdmin) {
        fetchUsers();
        if (selectedUser) {
          fetchMessages();
        }
      } else {
        fetchMessages();
        checkAndSendWelcomeMessage();
      }
      const msgChannel = subscribeToMessages();
      const statChannel = subscribeToUserStatus();
      updateUserStatus(true);

      return () => {
        updateUserStatus(false);
        msgChannel?.unsubscribe();
        statChannel?.unsubscribe();
      };
    }
  }, [isAdmin, user?.id, selectedUser?.id]);

  const updateUserStatus = async (isOnline: boolean) => {
    if (!user) return;

    const { error } = await supabase
      .from('user_status')
      .upsert({
        user_id: user.id,
        is_online: isOnline,
        last_seen: new Date().toISOString(),
      });

    if (error) console.error('Error updating status:', error);
  };

  const subscribeToUserStatus = () => {
    if (!ADMIN_ID) return null;

    const channel = supabase.channel('admin-status')
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'user_status',
          filter: `user_id=eq.${ADMIN_ID}`
        },
        (payload) => {
          setAdminStatus(payload.new as UserStatus);
        }
      )
      .subscribe();

    setStatusChannel(channel);
    return channel;
  };

  const fetchMessages = async () => {
    if (!user) return;

    const query = supabase
      .from('messages')
      .select('*')
      .or(
        isAdmin && selectedUser
          ? `and(sender_id.eq.${selectedUser.id},receiver_id.eq.${user.id}),and(sender_id.eq.${user.id},receiver_id.eq.${selectedUser.id})`
          : `sender_id.eq.${user.id},receiver_id.eq.${user.id}`
      )
      .order('created_at', { ascending: true });

    const { data, error } = await query;

    if (error) {
      console.error('Error fetching messages:', error);
      return;
    }

    setMessages(data || []);
    
    // Update unread count and mark messages as read if they're being viewed
    const unreadMessages = data?.filter(m => !m.read && m.receiver_id === user.id) || [];
    setUnreadCount(unreadMessages.length);

    if (unreadMessages.length > 0) {
      await markAsRead();
    }
  };

  const subscribeToMessages = () => {
    if (!user) return null;

    const channel = supabase.channel('messages')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'messages',
          filter: `receiver_id=eq.${user.id}`
        },
        async (payload) => {
          if (payload.eventType === 'INSERT') {
            const newMessage = payload.new as Message;
            setMessages(current => [...current, newMessage]);
            if (!newMessage.read) {
              setUnreadCount(count => count + 1);
            }
          } else if (payload.eventType === 'UPDATE') {
            const updatedMessage = payload.new as Message;
            setMessages(current =>
              current.map(msg =>
                msg.id === updatedMessage.id ? updatedMessage : msg
              )
            );
            
            // Recalculate unread count
            const { data } = await supabase
              .from('messages')
              .select('id')
              .eq('receiver_id', user.id)
              .eq('read', false);
            
            setUnreadCount(data?.length || 0);
          }
        }
      )
      .subscribe();

    setMessageChannel(channel);
    return channel;
  };

  const sendMessage = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user || !newMessage.trim()) return;
    
    const message = {
      sender_id: user.id,
      receiver_id: isAdmin ? selectedUser?.id : ADMIN_ID,
      content: newMessage.trim(),
      read: false,
      created_at: new Date().toISOString(),
    };

    if (!message.receiver_id) return;

    setMessages(prev => [...prev, message as Message]);
    setNewMessage('');

    const { error } = await supabase.from('messages').insert([message]);

    if (error) {
      console.error('Error sending message:', error);
      setMessages(prev => prev.filter(m => m !== message));
      return;
    }
  };

  const markAsRead = async () => {
    if (!user) return;

    try {
      // Update database first
      const { error } = await supabase
        .from('messages')
        .update({ read: true })
        .eq('receiver_id', user.id)
        .eq('read', false);

      if (error) {
        console.error('Error marking messages as read:', error);
        return;
      }

      // Then update local state
      setMessages(prevMessages => 
        prevMessages.map(msg => 
          msg.receiver_id === user.id ? { ...msg, read: true } : msg
        )
      );
      setUnreadCount(0);

      // Refresh users list if admin
      if (isAdmin) {
        fetchUsers();
      }
    } catch (error) {
      console.error('Error in markAsRead:', error);
    }
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const fetchUsers = async () => {
    if (!user || !isAdmin) return;
    
    // First get all messages
    const { data: messages, error: msgError } = await supabase
      .from('messages')
      .select('*')
      .or(`sender_id.neq.${ADMIN_ID},receiver_id.neq.${ADMIN_ID}`)
      .order('created_at', { ascending: false });

    if (msgError) {
      console.error('Error fetching messages:', msgError);
      return;
    }

    // Get unique user IDs from messages
    const userIds = [...new Set(
      messages?.map(m => m.sender_id === ADMIN_ID ? m.receiver_id : m.sender_id) || []
    )].filter(id => id !== ADMIN_ID);

    if (userIds.length === 0) {
      setUsers([]);
      return;
    }

    // Get user profiles
    const { data: profiles, error: profileError } = await supabase
      .from('user_profiles')
      .select('id, email, first_name')
      .in('id', userIds);

    if (profileError) {
      console.error('Error fetching profiles:', profileError);
      return;
    }

    const chatUsers = userIds.map(userId => {
      const userMessages = messages?.filter(m => 
        m.sender_id === userId || m.receiver_id === userId
      ) || [];
      const lastMessage = userMessages[0];
      const unreadCount = messages?.filter(m => 
        m.receiver_id === ADMIN_ID && 
        m.sender_id === userId && 
        !m.read
      ).length || 0;
      const profile = profiles?.find(p => p.id === userId);

      return {
        id: userId,
        email: profile?.email || '',
        firstName: profile?.first_name || 'User',
        last_message: lastMessage?.content,
        last_message_time: lastMessage?.created_at,
        unread_count: unreadCount
      };
    });

    setUsers(chatUsers);
  };

  const handleBackClick = () => {
    if (isAdmin) {
      setSelectedUser(null);
      setMessages([]); // Clear messages when going back to inbox
    } else {
      window.history.back();
    }
  };

  const sendWelcomeMessage = async (userId: string) => {
    if (!ADMIN_ID) return;

    const welcomeMessage = {
      sender_id: ADMIN_ID,
      receiver_id: userId,
      content: "Welcome to SimPLAB! 👋 I'm Dr. George. Feel free to ask any questions or leave comments if you need help. I'm here to assist you!",
      read: false,
      created_at: new Date().toISOString(),
    };

    const { error } = await supabase
      .from('messages')
      .insert([welcomeMessage]);

    if (error) {
      console.error('Error sending welcome message:', error);
    }
  };

  const checkAndSendWelcomeMessage = async () => {
    if (!user || !ADMIN_ID) return;

    // Check if user has received welcome message
    const { data: profile } = await supabase
      .from('user_profiles')
      .select('has_received_welcome')
      .eq('id', user.id)
      .single();

    if (!profile?.has_received_welcome) {
      // Send welcome message
      const welcomeMessage = {
        sender_id: ADMIN_ID,
        receiver_id: user.id,
        content: "👋 Hi! Welcome to SimPLAB - we're excited to have you here! Let us know if you need anything.",
        read: false,
        created_at: new Date().toISOString(),
        is_admin_message: true
      };

      const { error: messageError } = await supabase
        .from('messages')
        .insert([welcomeMessage]);

      if (!messageError) {
        // Update user profile to mark welcome message as sent
        await supabase
          .from('user_profiles')
          .update({ has_received_welcome: true })
          .eq('id', user.id);
      }
    }
  };

  if (isAdmin && !selectedUser) {
    return (
      <div className="chat-container">
        <div className="chat-header">
          <button className="back-button" onClick={() => window.history.back()}>
            <FaArrowLeft /> Go Back
          </button>
          <h2>Chat Inbox</h2>
        </div>
        <div className="users-list">
          {users.map(chatUser => (
            <div
              key={chatUser.id}
              className="user-item"
              onClick={() => setSelectedUser(chatUser)}
            >
              <div className="user-avatar">
                <FaUser />
              </div>
              <div className="user-info">
                <div className="user-name">
                  Dr. {chatUser.firstName}
                </div>
                <div className="last-message">
                  {chatUser.last_message || 'No messages yet'}
                </div>
              </div>
              {chatUser.unread_count > 0 && (
                <div className="unread-badge">
                  {chatUser.unread_count}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="chat-container">
      <div className="chat-header">
        <button className="back-button" onClick={handleBackClick}>
          <FaArrowLeft /> {isAdmin ? 'Back to Inbox' : 'Go Back'}
        </button>
        <h2>
          {isAdmin 
            ? <div className="user-header">
                <FaUser className="header-avatar" />
                <span>Dr. {selectedUser?.firstName || 'User'}</span>
              </div>
            : 'Chat with Admin'
          }
        </h2>
        {!isAdmin && unreadCount > 0 && (
          <div className="notification-badge">
            <FaBell />
            <span>{unreadCount}</span>
          </div>
        )}
      </div>

      {!isAdmin && (
        <div className="admin-status">
          <span className="online">● Admin is always online</span>
        </div>
      )}

      <div className="messages-container" onClick={markAsRead}>
        {messages.map((message) => (
          <div
            key={message.id || message.created_at}
            className={`message ${message.sender_id === user?.id ? 'sent' : 'received'}`}
          >
            <div className="message-content">{message.content}</div>
            <div className="message-time">
              {new Date(message.created_at).toLocaleDateString()} {new Date(message.created_at).toLocaleTimeString()}
            </div>
          </div>
        ))}
        <div ref={messagesEndRef} />
      </div>

      <form onSubmit={sendMessage} className="message-input">
        <input
          type="text"
          value={newMessage}
          onChange={(e) => setNewMessage(e.target.value)}
          placeholder="Type a message..."
        />
        <button type="submit">Send</button>
      </form>
    </div>
  );
} 
